/**
 * Zod input schemas: commissions. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
const CycleAmountsSchema = z.array(z.number().finite()).length(6);
const CommissionAssessmentRefSchema = z.object({
    fiscalYear: z.number().int().min(2000).max(2100),
    cycle: z.number().int().min(1).max(6),
});
export const CommissionUploadSourceSchema = CommissionAssessmentRefSchema.extend({
    kind: z.enum(['sfdc', 'memberships']),
    filename: z.string().trim().min(1).max(260),
    /** base64-encoded xlsx/csv. */
    fileBase64: z.string().min(1),
});
export const CommissionComputeReportSchema = CommissionAssessmentRefSchema;
export const CommissionGetReportSchema = CommissionAssessmentRefSchema;
export const CommissionMyStatementSchema = CommissionAssessmentRefSchema;
export const CommissionStatementPdfSchema = CommissionAssessmentRefSchema.extend({
    bdKey: z.string().trim().min(1).max(120),
});
export const CommissionConfirmPayoutSchema = CommissionAssessmentRefSchema.extend({
    /** Limit to these BDs; omitted/empty = all BDs in the report. */
    bdKeys: z.array(z.string().trim().min(1)).optional(),
});
export const CommissionReleaseStatementsSchema = CommissionAssessmentRefSchema.extend({
    bdKeys: z.array(z.string().trim().min(1)).optional(),
});
export const CommissionMyReleasesSchema = z.object({
    fiscalYear: z.number().int().min(2000).max(2100).optional(),
});
export const CommissionMyReleaseUrlSchema = z.object({ period: z.string().trim().min(1).max(40) });
export const CommissionMyTeamSchema = CommissionAssessmentRefSchema;
export const CommissionTeamReleaseUrlSchema = z.object({
    period: z.string().trim().min(1).max(40),
    bdKey: z.string().trim().min(1).max(120),
});
const CommissionFiscalYearSchema = z.object({
    fiscalYear: z.number().int().min(2000).max(2100),
});
export const CommissionAccrualGetSchema = CommissionFiscalYearSchema;
export const CommissionAccrualPreviewSchema = CommissionFiscalYearSchema;
// Extending the ledger to a quarter is per-assessment (fy + cycle).
export const CommissionAccrualExtendSchema = CommissionAssessmentRefSchema;
// Payout preview + the Accrual & Payout send are per-assessment (fy + cycle).
export const CommissionPayoutPreviewSchema = CommissionAssessmentRefSchema;
export const CommissionSendAccrualSchema = CommissionAssessmentRefSchema;
export const CommissionAccrualSendUrlSchema = z.object({
    period: z.string().trim().min(1).max(40),
    kind: z.enum(['accrual', 'payout']),
});
export const CommissionPullFromSalesforceSchema = CommissionAssessmentRefSchema.extend({
    kind: z.enum(['sfdc', 'memberships']),
});
// ── Agents layer ──
const MonthIdSchema = z.string().trim().regex(/^\d{4}-\d{2}$/, 'Month must be YYYY-MM');
export const CommissionAgentMonthSchema = z.object({ monthId: MonthIdSchema });
export const CommissionAgentUploadSchema = z.object({
    monthId: MonthIdSchema,
    filename: z.string().trim().min(1).max(260),
    fileBase64: z.string().min(1),
});
export const CommissionAgentManualLineSchema = z.object({
    client: z.string().trim().max(200),
    orderNumber: z.string().trim().max(120),
    paymentNumber: z.string().trim().max(120),
    costUnit: z.string().trim().max(60),
    amount: z.number().finite(),
    currency: z.string().trim().max(6),
    commission: z.number().finite().optional(),
});
export const CommissionAgentSaveAdjustmentSchema = z.object({
    monthId: MonthIdSchema,
    agentKey: z.string().trim().min(1).max(120),
    amount: z.number().finite(),
    reason: z.string().trim().max(500),
    excludedKeys: z.array(z.string().trim().max(200)).max(2000).optional(),
    manualLines: z.array(CommissionAgentManualLineSchema).max(200).optional(),
});
/** Post-lock correction: apply a manual ± + reason, recompute, re-lock payout, re-issue a Correction statement. */
export const CommissionAgentAdjustReissueSchema = z.object({
    monthId: MonthIdSchema,
    agentKey: z.string().trim().min(1).max(120),
    amount: z.number().finite(),
    reason: z.string().trim().min(1).max(500),
});
/** Stage 2 of the split correction flow: issue the ALREADY-APPLIED correction. */
export const CommissionAgentCorrectionIssueSchema = z.object({
    monthId: MonthIdSchema,
    agentKey: z.string().trim().min(1).max(120),
});
export const CommissionAgentSavePaidSchema = z.object({
    year: z.number().int().min(2000).max(2100),
    agentKey: z.string().trim().min(1).max(120),
    paidByMonth: z.array(z.number().finite()).length(12),
});
export const CommissionAgentStatementPdfSchema = z.object({
    monthId: MonthIdSchema,
    agentKey: z.string().trim().min(1).max(120),
});
export const CommissionAgentReleaseSchema = z.object({
    monthId: MonthIdSchema,
    agentKeys: z.array(z.string().trim().min(1).max(120)).max(500).optional(),
    /** Default true. When false, do everything (vault + mutation + record) but send NO email
     *  — for silently backfilling past months. */
    notify: z.boolean().optional(),
});
export const CommissionAgentLockPayoutSchema = CommissionAgentReleaseSchema;
// ── Commissions — OTHER layer ──
const QuarterIdSchema = z.string().trim().regex(/^\d{4}-Q[1-4]$/, 'Quarter must be YYYY-Qn');
export const CommissionOtherQuarterSchema = z.object({
    quarterId: QuarterIdSchema,
    planKey: z.string().trim().min(1).max(120),
});
export const CommissionOtherSaveAdjustmentSchema = CommissionOtherQuarterSchema.extend({
    amount: z.number().finite().min(-1_000_000).max(1_000_000),
    reason: z.string().trim().max(500),
    excludedTxnIds: z.array(z.string().trim().min(1).max(120)).max(2000),
});
export const CommissionOtherReleaseSchema = CommissionOtherQuarterSchema.extend({
    /** Default true. False = vault + record, no email (silent backfill). */
    notify: z.boolean().optional(),
});
export const CommissionOtherYearSchema = z.object({ year: z.number().int().min(2000).max(2100) });
export const CommissionAgentYearSchema = z.object({ year: z.number().int().min(2000).max(2100) });
export const CommissionAgentSetDeltaSchema = z.object({
    monthId: MonthIdSchema,
    agentKey: z.string().trim().min(1).max(120),
    /** SUM of the month's delta (legacy single-value clients send only this). */
    delta: z.number().finite(),
    /** Itemized lines (≤4); when present, `delta` is recomputed server-side as their sum.
     *  `balanceOnly` lines move the revolving balance WITHOUT affecting the payout. */
    entries: z.array(z.object({
        amount: z.number().finite(),
        note: z.string().trim().max(200),
        balanceOnly: z.boolean().optional(),
    })).max(4).optional(),
});
export const CommissionSavePaidSchema = z.object({
    fiscalYear: z.number().int().min(2000).max(2100),
    bdKey: z.string().trim().min(1).max(120),
    commissionPaidByCycle: CycleAmountsSchema,
    membershipPaidByCycle: CycleAmountsSchema,
});
//# sourceMappingURL=commissions.js.map